import { useState, useRef, useCallback, useEffect } from "react";
import type { EditorView } from "prosemirror-view";
import { useLocation } from "wouter";
import { CRTScreen } from "../components/CRTScreen.tsx";
import { NLSEditor } from "../components/NLSEditor.tsx";
import { AddressBar } from "../components/AddressBar.tsx";
import { StatusBar } from "../components/StatusBar.tsx";
import { CommandBar } from "../components/CommandBar.tsx";
import { ViewspecPanel } from "../components/ViewspecPanel.tsx";
import { SplitView } from "../components/SplitView.tsx";
import { HelpDialog } from "../components/HelpDialog.tsx";
import { useViewspecs } from "../hooks/useViewspecs.ts";
import { useDocument } from "../hooks/useDocument.ts";
import { useCommands } from "../hooks/useCommands.ts";
import {
  countStatements,
  maxDepth,
  countLinks,
} from "../documents/addressing.ts";

interface EditorPageProps {
  documentId: string;
}

export function EditorPage({ documentId }: EditorPageProps) {
  const [, setLocation] = useLocation();
  const { document } = useDocument(documentId);
  const { viewspecs, setViewspecs } = useViewspecs();
  const {
    viewspecs: splitViewspecs,
    setViewspecs: setSplitViewspecs,
  } = useViewspecs();

  const [currentSID, setCurrentSID] = useState("");
  const [showPanel, setShowPanel] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const editorViewRef = useRef<EditorView | null>(null);
  const splitEditorViewRef = useRef<EditorView | null>(null);

  const navigate = useCallback(
    (path: string) => setLocation(path),
    [setLocation]
  );

  const focusEditor = useCallback(() => {
    editorViewRef.current?.focus();
  }, []);

  const commands = useCommands({
    editorViewRef,
    currentDocument: document,
    navigate,
    setViewspecs,
    focusEditor,
  });

  const handleViewspecChange = useCallback(
    (vs: Partial<import("../viewspecs/types.ts").ViewspecState>) => {
      setViewspecs(vs);
    },
    [setViewspecs]
  );

  const toggleHelp = useCallback(() => {
    setShowHelp((p) => !p);
  }, []);

  // Global keyboard shortcuts for help (?) and viewspec panel (Ctrl+Shift+V)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "V") {
        e.preventDefault();
        setShowPanel((p) => !p);
      }
      // "?" only when not in an input/textarea and command bar is not active
      if (
        e.key === "?" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !commands.isActive &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        setShowHelp((p) => !p);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [commands.isActive]);

  if (!document) {
    return (
      <CRTScreen>
        <div
          style={{
            padding: "2rem",
            color: "var(--nls-error)",
          }}
        >
          Document not found: {documentId}
          <br />
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            style={{ color: "var(--nls-link)", marginTop: "1rem", display: "inline-block" }}
          >
            Back to document browser
          </a>
        </div>
      </CRTScreen>
    );
  }

  const stmtCount = countStatements(document.root);
  const depth = maxDepth(document.root);
  const linkCt = countLinks(document.root);

  const mainEditor = (
    <NLSEditor
      document={document}
      viewspecs={viewspecs}
      onViewspecChange={handleViewspecChange}
      onNavigate={navigate}
      onCursorMove={setCurrentSID}
      editorViewRef={editorViewRef}
      focusCommandBar={commands.focusCommandBar}
      executeCommand={commands.executeCommand}
    />
  );

  const splitEditor =
    viewspecs.splitMode !== "none" ? (
      <NLSEditor
        document={document}
        viewspecs={splitViewspecs}
        onViewspecChange={setSplitViewspecs}
        onNavigate={navigate}
        editorViewRef={splitEditorViewRef}
        focusCommandBar={commands.focusCommandBar}
        executeCommand={commands.executeCommand}
      />
    ) : null;

  return (
    <CRTScreen className="editor-page">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <AddressBar
          documentTitle={document.title}
          documentId={document.id}
          currentSID={currentSID}
          viewspecs={viewspecs}
        />

        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <div style={{ flex: 1, overflow: "hidden" }}>
            {splitEditor ? (
              <SplitView
                mode={viewspecs.splitMode}
                leftPane={mainEditor}
                rightPane={splitEditor}
              />
            ) : (
              <div style={{ height: "100%", overflow: "auto" }}>
                {mainEditor}
              </div>
            )}
          </div>

          <ViewspecPanel
            viewspecs={viewspecs}
            onChange={handleViewspecChange}
            visible={showPanel}
          />
        </div>

        <CommandBar
          barState={commands.barState}
          isActive={commands.isActive}
          currentPrompt={commands.currentPrompt}
          onSubmit={commands.submitInput}
          onCancel={commands.blurCommandBar}
        />

        <StatusBar
          statementCount={stmtCount}
          maxDepth={depth}
          linkCount={linkCt}
          mode={showPanel ? "VIEW" : "EDIT"}
          onHelpToggle={toggleHelp}
        />
      </div>

      <HelpDialog open={showHelp} onClose={() => setShowHelp(false)} />
    </CRTScreen>
  );
}
